import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Home,
  CreditCard,
  User,
  FileText,
} from "lucide-react";
import {
  redeemAccessCode,
  SOURCE_CONFIG,
  formatPhoneNumber,
  formatCurrency,
} from "../services/LeadsService";

// Source Badge Component
const SourceBadge = ({ source }) => {
  const config = SOURCE_CONFIG[source] || SOURCE_CONFIG.unknown;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bgClass} ${config.textClass}`}>
      {config.label}
    </span>
  );
};

const AccessCodePage = () => {
  const [accessCode, setAccessCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [redeemedLead, setRedeemedLead] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accessCode.trim() || accessCode.length !== 8) {
      setError("Please enter a valid 8-character access code");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await redeemAccessCode(accessCode.toUpperCase());
      if (result.success && result.lead) {
        setRedeemedLead(result.lead);
      }
    } catch (err) {
      // Show error but DO NOT redirect or deauth
      setError(err.message || "Failed to redeem access code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    setAccessCode(value);
    setError(null);
  };

  const handleBack = () => {
    setRedeemedLead(null);
    setAccessCode("");
  };

  // If lead is redeemed, show lead details
  if (redeemedLead) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
        >
          {/* Success Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Lead Access Granted</h1>
                <p className="text-emerald-100 text-sm">You now have permanent access to this lead</p>
              </div>
            </div>
          </div>

          {/* Lead Details */}
          <div className="p-6">
            {/* Contact Info Header */}
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white font-bold text-2xl">
                {redeemedLead.contact_name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{redeemedLead.contact_name}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <SourceBadge source={redeemedLead.source} />
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Contact Information
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                    <a href={`mailto:${redeemedLead.contact_email}`} className="text-sm text-sky-600 dark:text-sky-400 hover:underline">
                      {redeemedLead.contact_email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Phone</p>
                    <a href={`tel:${redeemedLead.contact_phone}`} className="text-sm text-sky-600 dark:text-sky-400 hover:underline">
                      {formatPhoneNumber(redeemedLead.contact_phone)}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Loan Details */}
            {redeemedLead.loan_details && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Loan Details
                </h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {redeemedLead.loan_details.loanPurpose && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Loan Purpose</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{redeemedLead.loan_details.loanPurpose}</p>
                    </div>
                  )}
                  {redeemedLead.loan_details.propertyType && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg flex items-start gap-2">
                      <Home className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Property Type</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{redeemedLead.loan_details.propertyType}</p>
                      </div>
                    </div>
                  )}
                  {redeemedLead.loan_details.propertyAddress && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg md:col-span-2 flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Property Address</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{redeemedLead.loan_details.propertyAddress}</p>
                      </div>
                    </div>
                  )}
                  {redeemedLead.loan_details.estimatedValue && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Estimated Value</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(redeemedLead.loan_details.estimatedValue)}</p>
                      </div>
                    </div>
                  )}
                  {redeemedLead.loan_details.downPayment && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Down Payment</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(redeemedLead.loan_details.downPayment)}</p>
                      </div>
                    </div>
                  )}
                  {redeemedLead.loan_details.creditScore && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg flex items-start gap-2">
                      <CreditCard className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Credit Score</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{redeemedLead.loan_details.creditScore}</p>
                      </div>
                    </div>
                  )}
                  {redeemedLead.loan_details.annualIncome && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Annual Income</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{formatCurrency(redeemedLead.loan_details.annualIncome)}</p>
                      </div>
                    </div>
                  )}
                  {redeemedLead.loan_details.occupancy && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                      <p className="text-xs text-slate-500 dark:text-slate-400">Occupancy</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{redeemedLead.loan_details.occupancy}</p>
                    </div>
                  )}
                </div>
                {redeemedLead.loan_details.additionalNotes && (
                  <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Additional Notes</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{redeemedLead.loan_details.additionalNotes}</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <a
                href="/"
                className="ml-auto flex items-center gap-2 px-6 py-2 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Access code entry form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Enter Access Code</h1>
          <p className="text-sky-100 mt-2">Unlock lead details with your unique code</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 text-center">
            Enter the 8-character access code from your email notification to view the full lead details.
          </p>

          {/* Access Code Input */}
          <div className="mb-6">
            <label htmlFor="accessCode" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 text-center">
              Access Code
            </label>
            <input
              id="accessCode"
              type="text"
              value={accessCode}
              onChange={handleInputChange}
              placeholder="T64JWF7S"
              className="w-full px-4 py-4 text-center text-3xl font-mono tracking-[0.5em] border-2 border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent uppercase"
              autoFocus
              autoComplete="off"
              spellCheck="false"
            />
            <div className="flex justify-center mt-3">
              <div className="flex gap-1">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i < accessCode.length ? "bg-sky-500" : "bg-slate-300 dark:bg-slate-600"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                {error}
              </div>
            </motion.div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || accessCode.length !== 8}
            className="w-full py-4 bg-sky-600 text-white rounded-xl font-semibold hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Unlock Lead Details
              </>
            )}
          </button>

          <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-4">
            After successful redemption, you'll have permanent access to this lead.
          </p>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 text-center">
          <a href="/" className="text-sm text-sky-600 dark:text-sky-400 hover:underline">
            Return to Dashboard
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AccessCodePage;
